import { toast } from '../utils/toastBus';

const STORAGE_KEYS = {
  postAuthRedirect: 'post_auth_redirect',
};

export function setPostAuthRedirect(path) {
  try {
    if (!path || typeof path !== 'string') return;
    window.localStorage.setItem(STORAGE_KEYS.postAuthRedirect, path);
  } catch {}
}

export function consumePostAuthRedirect() {
  try {
    const val = window.localStorage.getItem(STORAGE_KEYS.postAuthRedirect);
    if (val) window.localStorage.removeItem(STORAGE_KEYS.postAuthRedirect);
    return val || null;
  } catch {
    return null;
  }
}
const runtimeOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : null;
const inferredProdBase = (() => {
  try {
    if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
      const host = String(window.location.hostname || '');
      if (host.endsWith('loudest.ai')) return 'https://api.loudest.ai';
    }
  } catch {}
  return '';
})();
export const BASE = process.env.REACT_APP_API_BASE
  || process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? inferredProdBase : 'http://localhost:5001');

const AUDIO_EXT_REGEX = /\.(mp3|wav|m4a|aac|flac|ogg|oga|webm)(?:\?|$)/i;
const DISCOVER_DEMO_FILENAMES = new Set([
  'demo_loud-1761231193698.mp3',
  'demo_final-1761229956704.mp4',
  'demo_padded-loud-1761217322546.mp3'
]);

function shouldSuppressNotFoundToast(path, status) {
  try {
    if (status !== 404) return false;
    if (!path || typeof path !== 'string') return false;
    if (path === '/api/admin/system/analysis' || path === '/api/admin/system/analysis/run') return true;
    if (path === '/api/brand/profile') return true;
    if (!path.startsWith('/api/streams/')) return false;
    const filename = path.slice('/api/streams/'.length).split('?')[0];
    return DISCOVER_DEMO_FILENAMES.has(filename);
  } catch {
    return false;
  }
}

function rewriteApiStreamsPath(pathname) {
  try {
    if (!pathname || typeof pathname !== 'string') return null;
    if (!AUDIO_EXT_REGEX.test(pathname)) return null;
    if (pathname.startsWith('/api/streams/')) {
      const filename = pathname.slice('/api/streams/'.length);
      return filename ? `/uploads/${filename}` : null;
    }
    if (pathname.startsWith('api/streams/')) {
      const filename = pathname.slice('api/streams/'.length);
      return filename ? `/uploads/${filename}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

export function resolveMediaUrl(url) {
  try {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    if (/^https?:\/\//i.test(trimmed)) {
      try {
        const absolute = new URL(trimmed);
        const rewritten = rewriteApiStreamsPath(absolute.pathname);
        if (rewritten) {
          absolute.pathname = rewritten;
          return absolute.toString();
        }
      } catch {}
      return trimmed;
    }

    if (trimmed.startsWith('/uploads/')) {
      return `${BASE}${trimmed}`;
    }

    if (trimmed.startsWith('uploads/')) {
      return `${BASE}/${trimmed}`;
    }

    const baseLessQuery = trimmed.split('?')[0] || trimmed;
    if (baseLessQuery.includes('/uploads/projects/')) {
      const idx = baseLessQuery.indexOf('/uploads/projects/');
      const normalized = `${baseLessQuery.slice(idx)}${trimmed.slice(baseLessQuery.length)}`;
      return `${BASE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
    }

    const rewrittenRelative = rewriteApiStreamsPath(trimmed.startsWith('/') ? trimmed : `/${trimmed}`);
    if (rewrittenRelative) {
      return `${BASE}${rewrittenRelative}`;
    }

    const filenameOnly = trimmed.replace(/^\.\/+/, '');
    if (!trimmed.startsWith('/') && filenameOnly && !filenameOnly.startsWith('uploads/') && /\.[a-z0-9]{2,4}(?:\?|$)/i.test(filenameOnly)) {
      return `${BASE}/uploads/${filenameOnly}`;
    }

    if (trimmed.startsWith('/')) {
      return `${BASE}${trimmed}`;
    }

    return `${BASE}/${trimmed}`;
  } catch {
    return url;
  }
}

let token = null;
const __getCache = new Map();
const __inFlight = new Map();
export function setToken(t) { token = t; }
function isBackgroundGet(path) {
  try {
    return (
      /^\/api\/users\/me\b/.test(path) ||
      /^\/api\/subscriptions\/usage\/me\b/.test(path) ||
      /^\/api\/discover\b/.test(path) ||
      /^\/api\/streams\/popular\b/.test(path) ||
      /^\/api\/ads\/popular\b/.test(path)
    );
  } catch { return false; }
}
function authHeaders() {
  const h = { "Content-Type": "application/json", Accept: "application/json" };
  let t = token;
  if (!t) {
    try { t = window.localStorage.getItem('jwt'); } catch {}
  }
  if (!t) {
    try { t = window.sessionStorage.getItem('jwt'); } catch {}
  }
  if (t) h["Authorization"] = `Bearer ${t}`;
  return h;
}

// Normalize composer response shapes: { items } or { prompts } -> consistent { items: { images, audio, videos, music }, prompts }
function normalizeComposerAsset(asset) {
  if (!asset || typeof asset !== 'object') return asset;
  const next = { ...asset };
  const url = resolveMediaUrl(next.url || next.path || next.audioPath || '');
  const thumbUrl = resolveMediaUrl(next.thumbnailUrl || next.thumbnail_url || '');
  const waveUrl = resolveMediaUrl(next.waveformUrl || next.waveform_url || '');
  const durationCandidate = Number(next.durationMs ?? next.duration_ms);
  const previewStatusRaw = next.previewStatus || next.preview_status;
  const previewGeneratedAtRaw = next.previewGeneratedAt || next.preview_generated_at;
  next.url = url;
  next.thumbnailUrl = thumbUrl || null;
  next.waveformUrl = waveUrl || null;
  next.durationMs = Number.isFinite(durationCandidate) && durationCandidate >= 0 ? durationCandidate : null;
  next.previewStatus = typeof previewStatusRaw === 'string' && previewStatusRaw.trim() ? previewStatusRaw.trim() : null;
  next.previewGeneratedAt = typeof previewGeneratedAtRaw === 'string' && previewGeneratedAtRaw.trim() ? previewGeneratedAtRaw.trim() : null;
  return next;
}

function normalizeComposerResponse(res) {
  if (!res || typeof res !== 'object') return res;
  // Normalize assets shape: ensure items has expected keys (composer responses only)
  if (res.items && !Array.isArray(res.items) && typeof res.items === 'object') {
    res.items = {
      images: Array.isArray(res.items.images) ? res.items.images.map(normalizeComposerAsset) : [],
      audio: Array.isArray(res.items.audio) ? res.items.audio.map(normalizeComposerAsset) : [],
      videos: Array.isArray(res.items.videos) ? res.items.videos.map(normalizeComposerAsset) : [],
      music: Array.isArray(res.items.music) ? res.items.music : [],
      ...res.items
    };
  }
  if (Array.isArray(res.items) && res.items.every((it) => it && typeof it === 'object')) {
    res.items = res.items.map(normalizeComposerAsset);
  }
  if (res.item && typeof res.item === 'object' && typeof res.item.type === 'string') {
    res.item = normalizeComposerAsset(res.item);
  }
  // Normalize prompts shape: { prompts } -> { items: { prompts } }
  if (Array.isArray(res.prompts)) {
    res = { ...res, items: { ...res.items, prompts: res.prompts } };
  }
  return res;
}

function bustComposerAssetCache(projectId) {
  try {
    const prefix = `/api/composer/projects/${encodeURIComponent(String(projectId))}/assets`;
    for (const key of __getCache.keys()) {
      if (key.startsWith(prefix)) {
        __getCache.delete(key);
      }
    }
  } catch {}
}

function ttlFor(path) {
  try {
    if (/^\/api\/composer\/projects\/[^/]+\/assets/.test(path)) return 6_000;
    if (/^\/api\/streams\/popular/.test(path)) return 10_000;
    if (/^\/api\/ads\/popular/.test(path)) return 10_000;
    if (/^\/api\/discover/.test(path)) return 8_000;
    if (/^\/api\/streams\//.test(path)) return 3_000; // per-stream backfill
    return 2_000;
  } catch { return 2000; }
}

function attachHttpMeta(payload, status, path) {
  if (!payload || typeof payload !== 'object') return payload;
  const next = { ...payload };
  if (typeof status === 'number' && Number.isFinite(status) && next.status == null) {
    next.status = status;
  }
  if (typeof path === 'string' && path && !next.path) {
    next.path = path;
  }
  if (typeof next.ok !== 'boolean') {
    next.ok = !next.error && typeof status === 'number' && status >= 200 && status < 300;
  }
  return next;
}

export const api = {
  // System Analysis
  async getSystemAnalysis() { return this.get('/api/admin/system/analysis'); },
  async runSystemAnalysis() { return this.post('/api/admin/system/analysis/run'); },
  async get(path) {
    try {
      const now = Date.now();
      const cachedEntry = __getCache.get(path);
      if (cachedEntry && cachedEntry.expires > now) return cachedEntry.data;
      if (__inFlight.has(path)) return await __inFlight.get(path);
      const p = (async () => {
        const r = await fetch(`${BASE}${path}`, { headers: authHeaders(), credentials: 'include' });
        if (r.status === 304 && cachedEntry?.data) {
          __getCache.set(path, { expires: now + ttlFor(path), data: cachedEntry.data });
          __inFlight.delete(path);
          return cachedEntry.data;
        }
        if (r.status === 404 && shouldSuppressNotFoundToast(path, r.status)) {
          const suppressed = attachHttpMeta({ notFound: true }, r.status, path);
          __inFlight.delete(path);
          return suppressed;
        }
        const json = await r.json().catch(() => ({ error: 'parse_error' }));
        try {
          const warn = r.headers.get('x-usage-warning');
          if (warn) {
            const used = r.headers.get('x-usage-minutes');
            const limit = r.headers.get('x-usage-limit-minutes');
            toast({ type: 'warning', message: `You're nearing your free quota${limit ? ` (${used}/${limit} min)` : ''}. Consider creating an account.` });
            try { window.dispatchEvent(new CustomEvent('usage-warning', { detail: { used, limit } })); } catch {}
          }
        } catch {}
        if (!r.ok) {
          if (r.status === 429) {
            const ra = r.headers.get('retry-after');
            if (!isBackgroundGet(path)) {
              toast({ type: 'warning', message: `You're sending requests too quickly. Please wait${ra ? ` ~${ra}s` : ''} and try again.` });
            }
          } else if (r.status === 401 && !token) {
            // Suppress noise when unauthenticated
            __inFlight.delete(path);
            return { error: 'unauthorized' };
          } else {
            if (!isBackgroundGet(path) && !shouldSuppressNotFoundToast(path, r.status)) {
              toast({ type: 'error', message: `Request failed (${r.status}) for ${path}` });
            }
          }
        }
        if (r.ok) {
          const normalized = normalizeComposerResponse(json);
          __getCache.set(path, { expires: now + ttlFor(path), data: normalized });
        }
        const payload = attachHttpMeta(json, r.status, path);
        __inFlight.delete(path);
        return payload;
      })();
      __inFlight.set(path, p);
      const res = await p;
      return normalizeComposerResponse(res);
    } catch (e) {
      if (!isBackgroundGet(path)) {
        toast({ type: 'error', message: `Network error for ${path}` });
      }
      __inFlight.delete(path);
      return { error: 'network_error' };
    }
  },
  // Composer (projects)
  async composerCreateProject(payload) {
    const res = await fetch(`${BASE}/api/composer/projects`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(payload || {}) });
    return res.json().catch(() => ({}));
  },
  async composerListProjects() {
    const res = await fetch(`${BASE}/api/composer/projects`, { headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerGetProject(id) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}`, { headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerCreateAudio(id, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/audio`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(id);
    return normalized;
  },
  async composerCreateImage(id, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/image`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(id);
    return normalized;
  },
  async composerCreateAudioImage(id, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/audio-image`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(id);
    return normalized;
  },
  async composerUploadAsset(id, file, assetType) {
    try {
      if (!(file instanceof File || (typeof Blob !== 'undefined' && file instanceof Blob))) {
        return { error: 'invalid_file' };
      }
      const form = new FormData();
      form.append('file', file);
      if (assetType) form.append('assetType', assetType);
      const headers = authHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/assets/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: form
      });
      const json = await res.json().catch(() => ({}));
      const normalized = normalizeComposerResponse(json);
      if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(id);
      return normalized;
    } catch (err) {
      toast({ type: 'error', message: 'Upload failed' });
      return { error: 'upload_failed' };
    }
  },
  async composerCreateVideo(id, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/video`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(payload || {}) });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(id);
    return normalized;
  },
  async composerFinalize(id) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/finalize`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerRegenerate(id, type) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}/regenerate`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ type }) });
    return res.json().catch(() => ({}));
  },
  async composerGeneratePrompts(projectId, body) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/generate-prompts`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(body || {})
    });
    const json = await res.json().catch(() => ({}));
    return normalizeComposerResponse(json);
  },
  async composerPromptSuggestions(limit = 8) {
    const res = await fetch(`${BASE}/api/composer/prompt-suggestions?limit=${encodeURIComponent(String(limit))}`, { headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerCreateStandaloneAudio(payload) {
    const res = await fetch(`${BASE}/api/composer/audio`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(payload || {}) });
    return res.json().catch(() => ({}));
  },
  async composerUpdateProject(id, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}`, { method: 'PUT', headers: authHeaders(), credentials: 'include', body: JSON.stringify(payload || {}) });
    return res.json().catch(() => ({}));
  },
  async composerDeleteProject(id) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(id))}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerAssetCopy(assetId, toProjectId) {
    const res = await fetch(`${BASE}/api/composer/assets/${encodeURIComponent(String(assetId))}/copy`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ toProjectId }) });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(toProjectId);
    return normalized;
  },
  async composerAssetMove(assetId, toProjectId) {
    const res = await fetch(`${BASE}/api/composer/assets/${encodeURIComponent(String(assetId))}/move`, { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ toProjectId }) });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) {
      bustComposerAssetCache(toProjectId);
      const fromProjectId = json?.item?.projectId || json?.item?.project_id || null;
      if (fromProjectId) bustComposerAssetCache(fromProjectId);
    }
    return normalized;
  },
  async composerAssetArchive(assetId) {
    const res = await fetch(`${BASE}/api/composer/assets/${encodeURIComponent(String(assetId))}/archive`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerAssetRestore(assetId) {
    const res = await fetch(`${BASE}/api/composer/assets/${encodeURIComponent(String(assetId))}/restore`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerDeleteArchived(assetId) {
    const res = await fetch(`${BASE}/api/composer/archives/${encodeURIComponent(String(assetId))}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerAssetDelete(assetId) {
    const res = await fetch(`${BASE}/api/composer/assets/${encodeURIComponent(String(assetId))}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerListMyArchives() {
    const res = await fetch(`${BASE}/api/composer/archives`, { headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerDeleteMyArchives() {
    const res = await fetch(`${BASE}/api/composer/archives`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async composerDeleteArchive(assetId) {
    const res = await fetch(`${BASE}/api/composer/archives/${encodeURIComponent(String(assetId))}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  // Projects (P0)
  async createProject({ name, goals, demographics }) {
    const res = await fetch(`${BASE}/api/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, goals, demographics })
    });
    return res.json().catch(() => ({}));
  },
  async listProjects() {
    const res = await fetch(`${BASE}/api/projects`, { credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async getProject(projectId) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}`, { credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async getProjectAssets(projectId) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}/assets`, { credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async appendProjectAsset(projectId, payload) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}/assets`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async saveProjectRoute(projectId, payload) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}/routes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async getProjectRoutes(projectId) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}/routes`, { credentials: 'include' });
    return res.json().catch(() => ({}));
  },
  async createProjectVideo(projectId, payload) {
    const res = await fetch(`${BASE}/api/projects/${encodeURIComponent(String(projectId))}/video`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async composerVideoStart(projectId, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/video/start`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async composerVideoStatus(projectId, predictionId) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/video/status/${encodeURIComponent(String(predictionId))}`, {
      headers: authHeaders(),
      credentials: 'include'
    });
    return res.json().catch(() => ({}));
  },
  async composerVideoCancel(projectId, predictionId) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/video/cancel/${encodeURIComponent(String(predictionId))}`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include'
    });
    return res.json().catch(() => ({}));
  },
  async composerVideoFinalize(projectId, payload) {
    const res = await fetch(`${BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/video/finalize`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    const json = await res.json().catch(() => ({}));
    const normalized = normalizeComposerResponse(json);
    if (!normalized?.error && normalized?.ok !== false) bustComposerAssetCache(projectId);
    return normalized;
  },
  async adComposeSpec({ prompt, durationSec }) {
    return this.post('/api/adstudio/compose', { prompt, durationSec });
  },
  async adComposeFull({ prompt, durationSec, title }) {
    return this.post('/api/adstudio/compose/full', { prompt, durationSec, title });
  },
  async adSimulate({ prompt, durationSec, tier, aspectRatio, musicBed, musicTrack, sfx }) {
    return this.post('/api/adstudio/simulate', { prompt, durationSec, tier, aspectRatio, musicBed, musicTrack, sfx });
  },
  async adComposeVideo({ imageUrl, audioPath, durationSec, aspectRatio }) {
    return this.post('/api/adstudio/video', { imageUrl, audioPath, durationSec, aspectRatio });
  },
  async adVideoStart(payload) {
    return this.post('/api/adstudio/video/start', payload || {});
  },
  async adVideoStatus(predictionId) {
    return this.get(`/api/adstudio/video/status/${encodeURIComponent(String(predictionId))}`);
  },
  async adVideoFinalize({ predictionId, streamId, audioPath, imageUrl, durationSec, aspectRatio }) {
    return this.post('/api/adstudio/video/finalize', { predictionId, streamId, audioPath, imageUrl, durationSec, aspectRatio });
  },
  async adVideoRelease(predictionId) {
    return this.post('/api/adstudio/video/release', { predictionId });
  },
  async trackPageview({ path, referrer, anonId }) {
    try {
      return await this.post('/api/analytics/track', { path, referrer, anonId });
    } catch (e) {
      return { ok: false };
    }
  },
  async trackClientError(payload) {
    try {
      const res = await fetch(`${BASE}/api/analytics/client-error`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload || {}),
      });
      return { ok: res.ok };
    } catch {
      return { ok: false };
    }
  },
  // Generic named-event tracking. Rides on the pageview endpoint (no schema
  // change needed) by encoding the event name + props as a synthetic path,
  // e.g. "/events/generate_clicked?voiceId=abc" — queryable the same way
  // real pageviews are.
  async trackEvent(name, props = {}) {
    try {
      const eventName = String(name || 'unknown_event');
      const params = new URLSearchParams();
      Object.entries(props || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v));
      });
      const qs = params.toString();
      const path = `/events/${eventName}${qs ? `?${qs}` : ''}`;
      const referrer = typeof window !== 'undefined' ? window.location?.pathname : undefined;
      const anonId = typeof window !== 'undefined' ? window.localStorage.getItem('anon_id') : undefined;
      return await this.trackPageview({ path, referrer, anonId });
    } catch (e) {
      return { ok: false };
    }
  },
  async getMyCredits() {
    return this.get('/api/credits/me');
  },
  async adminGrantCredits({ userId, amount }) {
    return this.post('/api/credits/grant', { userId, amount });
  },
  async checkoutCredits(packId) {
    return this.post(`/api/payments/credits/${encodeURIComponent(String(packId))}`, {});
  },
  async startCreditsPurchase(packId = '100') {
    try {
      const me = await this.me();
      if (!me || me.error) {
        return { ok: false, requiresAuth: true };
      }
      const checkout = await this.checkoutCredits(packId);
      if (checkout?.url) {
        window.location.href = checkout.url;
        return { ok: true, redirected: true };
      }
      if (checkout?.error === 'auth_required' || checkout?.status === 401) {
        return { ok: false, requiresAuth: true };
      }
      return { ok: false, error: checkout?.error || 'checkout_unavailable' };
    } catch {
      return { ok: false, requiresAuth: true };
    }
  },
  async onboardingCreateSession(payload) {
    const res = await fetch(`${BASE}/api/onboarding/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async onboardingGeneratePrompts(payload) {
    const res = await fetch(`${BASE}/api/onboarding/generate-prompts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async onboardingCompose(payload) {
    const res = await fetch(`${BASE}/api/onboarding/compose`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async onboardingVideoStart(payload) {
    const res = await fetch(`${BASE}/api/onboarding/video/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async onboardingVideoFinalize(payload) {
    const res = await fetch(`${BASE}/api/onboarding/video/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload || {})
    });
    return res.json().catch(() => ({}));
  },
  async onboardingSessionStatus(sessionId) {
    const res = await fetch(`${BASE}/api/onboarding/session/${encodeURIComponent(String(sessionId))}/status`, {
      headers: authHeaders(),
      credentials: 'include'
    });
    return res.json().catch(() => ({}));
  },
  // Brand Workspace
  async brandGetProfile() {
    return this.get('/api/brand/profile');
  },
  async brandSaveProfile(payload) {
    try {
      const res = await this.post('/api/brand/profile', payload || {});
      return res;
    } catch {
      return { error: 'brand_profile_save_failed' };
    }
  },
  async brandStartImport(payload) {
    try {
      const res = await this.post('/api/brand/import', payload || {});
      return res;
    } catch {
      return { error: 'brand_import_failed' };
    }
  },
  async brandImportStatus(importId) {
    try {
      if (!importId) return { error: 'import_id_required' };
      return await this.get(`/api/brand/import/${encodeURIComponent(String(importId))}`);
    } catch {
      return { error: 'brand_import_status_failed' };
    }
  },
  async post(path, body) {
    try {
      const r = await fetch(`${BASE}${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body), credentials: 'include' });
      const json = await r.json().catch(() => ({ error: 'parse_error' }));
      try {
        const warn = r.headers.get('x-usage-warning');
        if (warn) {
          const used = r.headers.get('x-usage-minutes');
          const limit = r.headers.get('x-usage-limit-minutes');
          toast({ type: 'warning', message: `You're nearing your free quota${limit ? ` (${used}/${limit} min)` : ''}.` });
          try { window.dispatchEvent(new CustomEvent('usage-warning', { detail: { used, limit } })); } catch {}
        }
      } catch {}
      if (!r.ok) {
        const quiet = json?.error === 'payment_required' || json?.error === 'stripe_unavailable' || json?.error === 'already_claimed' || json?.error === 'custom_plan' || json?.error === 'plan_not_billable' || json?.error === 'plan_not_found';
        if (r.status === 429) {
          const ra = r.headers.get('retry-after');
          toast({ type: 'warning', message: `You're sending requests too quickly. Please wait${ra ? ` ~${ra}s` : ''} and try again.` });
        } else if (r.status === 401 && !token) {
          return { error: 'unauthorized' };
        } else if (!quiet) {
          toast({ type: 'error', message: `Request failed (${r.status}) for ${path}` });
        }
      }
      return attachHttpMeta(json, r.status, path);
    } catch (e) {
      toast({ type: 'error', message: `Network error for ${path}` });
      return { error: 'network_error' };
    }
  },
  async patch(path, body) {
    try {
      const r = await fetch(`${BASE}${path}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body), credentials: 'include' });
      const json = await r.json().catch(() => ({ error: 'parse_error' }));
      try {
        const warn = r.headers.get('x-usage-warning');
        if (warn) {
          const used = r.headers.get('x-usage-minutes');
          const limit = r.headers.get('x-usage-limit-minutes');
          toast({ type: 'warning', message: `You're nearing your free quota${limit ? ` (${used}/${limit} min)` : ''}.` });
        }
      } catch {}
      if (!r.ok) {
        if (r.status === 401 && !token) return { error: 'unauthorized' };
        toast({ type: 'error', message: `Request failed (${r.status}) for ${path}` });
      }
      return json;
    } catch (e) {
      toast({ type: 'error', message: `Network error for ${path}` });
      return { error: 'network_error' };
    }
  },
  async delete(path) {
    try {
      const r = await fetch(`${BASE}${path}`, { method: "DELETE", headers: authHeaders(), credentials: 'include' });
      const json = await r.json().catch(() => ({ error: 'parse_error' }));
      try {
        const warn = r.headers.get('x-usage-warning');
        if (warn) {
          const used = r.headers.get('x-usage-minutes');
          const limit = r.headers.get('x-usage-limit-minutes');
          toast({ type: 'warning', message: `You're nearing your free quota${limit ? ` (${used}/${limit} min)` : ''}.` });
        }
      } catch {}
      if (!r.ok) {
        if (r.status === 429) {
          const ra = r.headers.get('retry-after');
          toast({ type: 'warning', message: `You're sending requests too quickly. Please wait${ra ? ` ~${ra}s` : ''} and try again.` });
        } else {
          toast({ type: 'error', message: `Request failed (${r.status}) for ${path}` });
        }
      }
      return json;
    } catch (e) {
      toast({ type: 'error', message: `Network error for ${path}` });
      return { error: 'network_error' };
    }
  },

  // Upload arbitrary file to /api/uploads using multipart/form-data
  // Optionally accepts onProgress(percent) callback; falls back to fetch if XHR not available
  async uploadFile(file, onProgress) {
    const fd = new FormData();
    fd.append('file', file);
    const authHeader = token ? { key: 'Authorization', value: `Bearer ${token}` } : null;
    // If progress is desired and XHR exists, use it
    if (typeof onProgress === 'function' && typeof XMLHttpRequest !== 'undefined') {
      try {
        const xhr = new XMLHttpRequest();
        return await new Promise((resolve, reject) => {
          xhr.open('POST', `${BASE}/api/uploads`);
          if (authHeader) xhr.setRequestHeader(authHeader.key, authHeader.value);
          xhr.upload.onprogress = (evt) => {
            if (evt && evt.lengthComputable) {
              const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
              try { onProgress(pct); } catch {}
            }
          };
          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              try {
                const json = JSON.parse(xhr.responseText || '{}');
                if (xhr.status >= 200 && xhr.status < 300) resolve(json);
                else resolve(json); // keep same shape, caller checks .error
              } catch (e) {
                resolve({ error: 'upload_parse_error' });
              }
            }
          };
          xhr.onerror = () => { try { toast({ type: 'error', message: 'Upload failed' }); } catch {} ; resolve({ error: 'upload_failed' }); };
          xhr.send(fd);
        });
      } catch (e) {
        // Fallback below
      }
    }
    // Fallback: fetch without progress
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const r = await fetch(`${BASE}/api/uploads`, { method: 'POST', headers, body: fd, credentials: 'include' });
      const json = await r.json().catch(() => ({ error: 'parse_error' }));
      if (!r.ok) toast({ type: 'error', message: `Upload failed (${r.status})` });
      return json;
    } catch (e) {
      toast({ type: 'error', message: 'Network error during upload' });
      return { error: 'network_error' };
    }
  },

  // Auth
  async register(payload) { return this.post('/api/auth/register', payload); },
  async login(payload) { return this.post('/api/auth/login', payload); },

  // Streams
  async createStream(payload) { return this.post('/api/streams', payload); },
  async createStreamAsync(payload) { return this.post('/api/streams/async', payload); },
  async checkoutAd(payload) { return this.post('/api/payments/ad', payload || {}); },
  async claimAd(payload) { return this.post('/api/payments/ad/claim', payload || {}); },
  async checkoutVideoAd(payload) { return this.post('/api/payments/video-ad', payload || {}); },
  async claimVideoAd(payload) { return this.post('/api/payments/video-ad/claim', payload || {}); },
  async getJob(id) { return this.get(`/api/jobs/${encodeURIComponent(String(id))}`); },
  async listStreams() { return this.get('/api/streams'); },
  async getStream(id) { return this.get(`/api/streams/${encodeURIComponent(String(id))}`); },
  async claimStream(id) { return this.post(`/api/streams/${encodeURIComponent(String(id))}/claim`); },
  async listPopularStreams(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/streams/popular${qs}`);
  },
  async likeStream(id) { return this.post(`/api/streams/${encodeURIComponent(String(id))}/like`, {}); },
  async unlikeStream(id) { return this.delete(`/api/streams/${encodeURIComponent(String(id))}/like`); },
  async listDiscover(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/discover${qs}`);
  },
  async listDiscoverProjects(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/discover/projects${qs}`);
  },
  async listMyStreams(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/streams/mine${qs}`);
  },
  async moderateStream(id, { status, reason }) { return this.post(`/api/streams/${encodeURIComponent(String(id))}/moderate`, { status, reason }); },
  async reportStream(id, { reason }) { return this.post(`/api/streams/${encodeURIComponent(String(id))}/report`, { reason }); },
  async deleteStream(id) { return this.delete(`/api/streams/${encodeURIComponent(String(id))}`); },
  async updateStreamImage(id, { imageUrl }) { return this.patch(`/api/streams/${encodeURIComponent(String(id))}/image`, { imageUrl }); },

  // Ads
  async listAds(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/ads${qs}`);
  },
  async listPopularAds(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/ads/popular${qs}`);
  },
  async getAd(id) { return this.get(`/api/ads/${id}`); },
  async createAd(payload) { return this.post('/api/ads', payload); },
  async updateAd(id, payload) { return this.patch(`/api/ads/${id}`, payload); },
  async deleteAd(id) { return this.delete(`/api/ads/${id}`); },
  async likeAd(id) { return this.post(`/api/ads/${encodeURIComponent(String(id))}/like`, {}); },
  async unlikeAd(id) { return this.delete(`/api/ads/${encodeURIComponent(String(id))}/like`); },

  // Ad Videos
  async getAdVideo(id) { return this.get(`/api/advideos/${encodeURIComponent(String(id))}`); },
  async getAdVideosByStream(streamId, limit = 20) { return this.get(`/api/advideos/by-stream/${encodeURIComponent(String(streamId))}?limit=${encodeURIComponent(String(limit))}`); },
  async getRecentAdVideos(limit = 12) { return this.get(`/api/advideos/recent?limit=${encodeURIComponent(String(limit))}`); },

  // Uploads (admin)
  async listUploads(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/uploads${qs}`);
  },
  async deleteUploadAdmin(name) {
    const safe = encodeURIComponent(name);
    return this.delete(`/api/uploads/${safe}`);
  },

  // Uploads (current user)
  async listMyUploads(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/uploads/mine${qs}`);
  },
  async deleteMyUpload(name) {
    const safe = encodeURIComponent(name);
    return this.delete(`/api/uploads/mine/${safe}`);
  },

  // Users
  async me() { return this.get('/api/users/me'); },
  async updateMe(payload) { return this.patch('/api/users/me', payload); },
  async listUsers() { return this.get('/api/users'); },
  async getUser(id) { return this.get(`/api/users/${id}`); },
  async updateUser(id, payload) { return this.patch(`/api/users/${id}`, payload); },
  async deleteUser(id) { return this.delete(`/api/users/${id}`); },

  // AI DJ
  async djVoices() { return this.get('/api/dj/voices'); },
  async djSpeak({ text, voiceId, voiceName, candidates, voiceObj }) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const r = await fetch(`${BASE}/api/dj/speak`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        voiceId,
        voiceName,
        voice: voiceId || voiceName,
        candidates: Array.isArray(candidates) ? candidates.filter(Boolean) : undefined,
        voiceObj: voiceObj || undefined
      })
    });
    const contentType = r.headers.get('content-type') || 'application/octet-stream';
    const buf = await r.arrayBuffer();
    if (!r.ok) {
      return { error: `HTTP ${r.status}`, contentType, blob: new Blob([buf], { type: contentType }) };
    }
    return { contentType, blob: new Blob([buf], { type: contentType }) };
  },

  // Clips
  async createClip(payload) { return this.post('/api/clips', payload); },
  async listClips(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/clips${qs}`);
  },
  async listRecentClips(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/clips/public${qs}`);
  },
  async updateClip(id, payload) { return this.patch(`/api/clips/${encodeURIComponent(String(id))}`, payload); },
  async deleteClip(id) { return this.delete(`/api/clips/${encodeURIComponent(String(id))}`); },
  async createClipsBatch(segments) { return this.post('/api/clips/batch', { segments }); },

  // Amplification
  async getAmplifyPresets() { return this.get('/api/amplify/presets'); },
  async getAmplifyConfig() { return this.get('/api/amplify/config'); },
  async amplifyAudio(payload) { return this.post('/api/amplify', payload); },
  async listRecentAmplified(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/amplify/recent${qs}`);
  },
  async createAmplifyPreset(payload) { return this.post('/api/amplify/presets', payload); },
  async deleteAmplifyPreset(name) { return this.delete(`/api/amplify/presets/${encodeURIComponent(name)}`); },

  // Media
  async concatAudio({ sources }) { return this.post('/api/media/concat', { sources }); },
  async transcribe({ sourceUrl }) { return this.post('/api/media/transcribe', { sourceUrl }); },
  async getTranscript(id) { return this.get(`/api/media/transcripts/${encodeURIComponent(String(id))}`); },
  async generateImage({ prompt, model, aspect_ratio }) { return this.post('/api/media/image/generate', { prompt, model, aspect_ratio }); },
  async exportVideo({ audioUrl, frames, size, fps }) { return this.post('/api/media/video/export', { audioUrl, frames, size, fps }); },
  // Streams helpers
  async previewFeed(url) { return this.get(`/api/streams/feed/preview?url=${encodeURIComponent(url)}`); },
  async signDownload(path, ttlMs) { return this.post('/api/uploads/sign', { path, ttlMs }); },
  async previewSourceHN(params) {
    try {
      const q = new URLSearchParams({
        tags: String(params?.tags || 'front_page'),
        hitsPerPage: String(Math.max(1, Math.min(50, Number(params?.hitsPerPage || 10))))
      }).toString();
      return await this.get(`/api/streams/source/hn?${q}`);
    } catch { return { error: 'preview_failed' }; }
  },
  async previewSourceArxiv(params) {
    try {
      const q = new URLSearchParams({
        search: String(params?.search || 'cs.AI'),
        max: String(Math.max(1, Math.min(50, Number(params?.max || 10))))
      }).toString();
      return await this.get(`/api/streams/source/arxiv?${q}`);
    } catch { return { error: 'preview_failed' }; }
  },
  async previewSourceUSGS(params) {
    try {
      const q = new URLSearchParams({
        minmag: String(Number(params?.minmag ?? 4.5)),
        hours: String(Math.max(1, Math.min(168, Number(params?.hours || 24)))),
        limit: String(Math.max(1, Math.min(50, Number(params?.limit || 10))))
      }).toString();
      return await this.get(`/api/streams/source/usgs?${q}`);
    } catch { return { error: 'preview_failed' }; }
  },
  // Metrics (admin)
  async getMetrics() { return this.get('/api/metrics'); },
  async getAdsAnalytics() { return this.get('/api/analytics/ads'); },
  // Subscriptions (admin)
  async listPlans() { return this.get('/api/subscriptions/plans'); },
  async upsertPlan(payload) { return this.post('/api/subscriptions/plans', payload); },
  async getUserSubscription(userId) { return this.get(`/api/subscriptions/users/${encodeURIComponent(userId)}`); },
  async assignUserSubscription(userId, planId) { return this.post(`/api/subscriptions/users/${encodeURIComponent(userId)}`, { planId }); },
  // Usage
  async getMyUsage() { return this.get('/api/subscriptions/usage/me'); },
  async getUserUsage(userId) { return this.get(`/api/subscriptions/usage/${encodeURIComponent(userId)}`); },
  // Admin Config
  async getAdminConfig() { return this.get('/api/admin/config'); },
  async setAdminConfig(patch) { return this.post('/api/admin/config', patch); },
  // Payments
  async listCheckoutPlans() { return this.get('/api/payments/plans'); },
  async startCheckout(planId) { return this.post(`/api/payments/checkout/${encodeURIComponent(String(planId))}`); }
};
export default api;
